<template>
  <b-overlay :show="loading">
    <div class="vx-card">
      <div class="vx-card__collapsible-content">
        <div class="vx-card__body row">
          <!-- Listado de integraciones disponibles -->
          <div class="col-md-4 col-12">
            <h4 class="text-primary mb-3">
              Integraciones
            </h4>
            <div class="input-group mb-3">
              <input
                v-model="search"
                type="text"
                class="form-control"
                placeholder="Buscar integración"
                aria-label="Buscar integración"
                aria-describedby="input-search-integration"
              >
            </div>
            <ul
              class="list-group mt-2"
              style="max-height: 80vh; overflow: auto;"
            >
              <li
                v-for="integration in filteredIntegrations"
                :key="'integration-' + integration.key"
                :id="'integration-' + integration.key"
                class="list-group-item integration-row"
                :class="{ 'active-integration': isSelected(integration) }"
                @click="selectIntegration(integration)"
                data-cy="integration-item"
              >
                <div class="d-flex align-items-center">
                  <feather-icon
                    :icon="integration.icon || 'LinkIcon'"
                    size="18"
                    class="mr-2"
                  />
                  <div>
                    <div class="font-weight-bold">{{ integration.name }}</div>
                    <small
                      v-if="integration.description"
                      class="d-block text-muted"
                    >
                      {{ integration.description }}
                    </small>
                  </div>
                </div>
                <b-badge
                  :variant="integrationBadge(integration).variant"
                  pill
                >
                  {{ integrationBadge(integration).text }}
                </b-badge>
              </li>
              <li
                v-if="!filteredIntegrations.length"
                class="list-group-item text-center text-muted"
              >
                No se encontraron integraciones
              </li>
            </ul>
          </div>

          <!-- Panel de configuración de la integración seleccionada -->
          <div
            class="col-md-8 col-12"
            v-if="selectedIntegration"
          >
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h4 class="text-primary mb-0">
                Configuración: {{ selectedIntegration.name }}
              </h4>
            </div>

            <p
              v-if="selectedIntegration.description"
              class="text-muted"
            >
              {{ selectedIntegration.description }}
            </p>

            <!-- Sección informativa (datos a entregar al proveedor) -->
            <b-card
              v-if="selectedIntegration.info"
              class="info-section mb-4"
              no-body
            >
              <div class="info-section__header">
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="mr-1"
                />
                <strong>{{ selectedIntegration.info.title }}</strong>
              </div>
              <div class="info-section__body">
                <p
                  v-if="selectedIntegration.info.description"
                  class="text-muted mb-3"
                >
                  {{ selectedIntegration.info.description }}
                </p>
                <div
                  v-for="group in selectedIntegration.info.groups"
                  :key="group.name"
                  class="mb-3"
                >
                  <div class="info-section__group-name">{{ group.name }}</div>
                  <div
                    v-for="item in group.items"
                    :key="group.name + '-' + item.label"
                    class="info-section__item"
                  >
                    <span class="info-section__label">{{ item.label }}:</span>
                    <code class="info-section__value">{{ item.value }}</code>
                    <feather-icon
                      icon="CopyIcon"
                      size="15"
                      class="info-section__copy"
                      v-tooltip="'Copiar'"
                      @click="copyToClipboard(item.value)"
                    />
                  </div>
                </div>
              </div>
            </b-card>

            <form
              data-vv-scope="integration"
              class="w-100"
            >
              <b-form-group
                v-for="field in selectedIntegration.fields"
                :key="field.key"
                :label="field.label"
              >
                <!-- Texto / password / número / url -->
                <b-form-input
                  v-if="isSimpleInput(field)"
                  :type="inputType(field)"
                  :name="field.key"
                  class="w-100"
                  :placeholder="field.placeholder || ''"
                  v-model="form.values[field.key]"
                  v-validate="field.required ? 'required' : ''"
                  :data-vv-as="field.label"
                  :state="validationState(field)"
                />

                <!-- Área de texto -->
                <b-form-textarea
                  v-else-if="field.type === 'textarea'"
                  :name="field.key"
                  rows="3"
                  :placeholder="field.placeholder || ''"
                  v-model="form.values[field.key]"
                  v-validate="field.required ? 'required' : ''"
                  :data-vv-as="field.label"
                  :state="validationState(field)"
                />

                <!-- Selección -->
                <b-form-select
                  v-else-if="field.type === 'select'"
                  :name="field.key"
                  v-model="form.values[field.key]"
                  :options="field.options || []"
                  v-validate="field.required ? 'required' : ''"
                  :data-vv-as="field.label"
                  :state="validationState(field)"
                />

                <!-- Booleano -->
                <b-form-checkbox
                  v-else-if="field.type === 'boolean'"
                  :name="field.key"
                  v-model="form.values[field.key]"
                  switch
                >
                  {{ form.values[field.key] ? 'Sí' : 'No' }}
                </b-form-checkbox>

                <!-- Texto por defecto -->
                <b-form-input
                  v-else
                  :name="field.key"
                  class="w-100"
                  :placeholder="field.placeholder || ''"
                  v-model="form.values[field.key]"
                />

                <small
                  v-if="field.help"
                  class="form-text text-muted"
                >
                  {{ field.help }}
                </small>
                <FormError
                  :attributeName="'integration.' + field.key"
                  :errorsForm="errors"
                />
              </b-form-group>

              <div
                v-if="!selectedIntegration.fields || !selectedIntegration.fields.length"
                class="text-muted"
              >
                Esta integración aún no tiene campos de configuración definidos.
              </div>

              <div class="text-right mt-3">
                <b-button
                  variant="primary"
                  :disabled="saving"
                  @click="saveIntegration()"
                  data-cy="save-integration"
                >
                  <b-spinner
                    v-if="saving"
                    small
                    class="mr-1"
                  />
                  Guardar
                </b-button>
              </div>
            </form>
          </div>

          <!-- Estado vacío -->
          <div
            class="col-md-8 col-12 d-flex align-items-center justify-content-center text-muted"
            v-else
            style="min-height: 300px;"
          >
            Selecciona una integración para configurar sus valores
          </div>
        </div>
      </div>
    </div>
  </b-overlay>
</template>

<script>
import { mapGetters } from 'vuex';

import FormError from '@/febos/global/documentManagement/components/FormError';
import CopyToClipboard from '@/febos/chile/approvals/mixins/CopyToClipboard';
import { ioSaveParameter } from '@/febos/servicios/api/herramientas.api';
import { ioGetSettings } from '@/febos/servicios/api/configuraciones.api';

/**
 * Definición de las integraciones disponibles.
 * Cada integración declara su `key`, `name`, `description`, `icon` y la lista de
 * `fields` que se renderizan en el panel de configuración.
 *
 * El `key` de cada campo corresponde al `parametro_id` real almacenado en la
 * tabla `opciones` (grupo `parametros`) de Febos, de modo que al persistir se
 * use directamente ese identificador.
 *
 * Tipos de campo soportados: text, password, number, url, email, textarea,
 * select (con `options`), boolean.
 */
const AVAILABLE_INTEGRATIONS = [
  {
    key: 'claveunica',
    name: 'Clave Única',
    description: 'Autenticación de usuarios mediante Clave Única del Estado.',
    icon: 'KeyIcon',
    info: {
      title: 'Datos a entregar a Clave Única',
      description: 'URLs de redirección y cierre de sesión que deben registrarse ante Clave Única según el ambiente.',
      groups: [
        {
          name: 'Pruebas',
          items: [
            { label: 'Redirect', value: 'https://api.febos.cl/pruebas/usuarios/sso/claveunica/autorize' },
            { label: 'Logout', value: 'https://api.febos.cl/pruebas/usuarios/sso/claveunica/logout' }
          ]
        },
        {
          name: 'Produccion',
          items: [
            { label: 'Redirect', value: 'https://api.febos.cl/produccion/usuarios/sso/claveunica/autorize' },
            { label: 'Logout', value: 'https://api.febos.cl/produccion/usuarios/sso/claveunica/logout' }
          ]
        }
      ]
    },
    fields: [
      {
        key: 'io.ingreso.sso.claveunica.cliente_id',
        label: 'Cliente Id',
        type: 'text',
        required: true,
        help: 'Id del cliente web, utilizado para identificarse ante los servicios'
      },
      {
        key: 'io.ingreso.sso.claveunica.secret',
        label: 'Secret',
        type: 'password',
        required: true,
        help: 'Secreto del cliente, equivalente a una contraseña, se utiliza para identificarse'
      },
      {
        key: 'io.ingreso.sso.claveunica.redirect',
        label: 'Redirect',
        type: 'select',
        required: false,
        help: 'Url de redirección con respuesta de Clave Única',
        options: [
          { text: 'Desarrollo', value: 'https%3A%2F%2Fapi.febos.cl%2Fdesarrollo%2Fusuarios%2Fsso%2Fclaveunica%2Fautorize' },
          { text: 'Pruebas', value: 'https%3A%2F%2Fapi.febos.cl%2Fpruebas%2Fusuarios%2Fsso%2Fclaveunica%2Fautorize' },
          { text: 'Certificacion', value: 'https%3A%2F%2Fapi.febos.cl%2Fcertificacion%2Fusuarios%2Fsso%2Fclaveunica%2Fautorize' },
          { text: 'Produccion', value: 'https%3A%2F%2Fapi.febos.cl%2Fproduccion%2Fusuarios%2Fsso%2Fclaveunica%2Fautorize' }
        ]
      }
    ]
  },
  {
    key: 'docdigital',
    name: 'Doc Digital',
    description: 'Integración con el servicio DocDigital del Estado.',
    icon: 'FileTextIcon',
    fields: [
      {
        key: 'io.externo.docdigital.url',
        label: 'Url del api',
        type: 'select',
        required: true,
        help: 'URL donde se obtienen datos desde el api',
        options: [
          { text: 'PRODUCCION', value: 'https://api-doc.digital.gob.cl' },
          { text: 'QA', value: 'https://api-demodoc.digital.gob.cl' },
          { text: 'QA V3', value: 'https://api-mw-docdigital.cert.digital.gob.cl' },
          { text: 'PRODUCCION V3', value: 'https://api-mw-docdigital.test.digital.gob.cl' }
        ]
      },
      {
        key: '__docdigital_user',
        label: 'Usuario',
        type: 'text',
        required: true,
        virtual: true,
        help: 'Usuario de las credenciales de Doc Digital'
      },
      {
        key: '__docdigital_pass',
        label: 'Clave',
        type: 'password',
        required: true,
        virtual: true,
        help: 'Clave (API Key) de las credenciales de Doc Digital'
      },
      {
        key: 'io.externo.docdigital.id_entidad',
        label: 'ID Entidad',
        type: 'number',
        required: false,
        help: 'ID de entidad en doc digital'
      }
    ],
    // Campos que se combinan en un único parámetro real al guardar/cargar.
    composites: [
      {
        // Parámetro real persistido en el backend (base64 de "usuario:clave").
        target: 'io.externo.docdigital.auth',
        userField: '__docdigital_user',
        passField: '__docdigital_pass'
      }
    ]
  },
  {
    key: 'segpres',
    name: 'Firmagob (Segpres)',
    description: 'Firma electrónica avanzada mediante Firmagob / Segpres.',
    icon: 'EditIcon',
    fields: [
      {
        key: 'io.firma.segpres.url',
        label: 'URL de firma segpres',
        type: 'select',
        required: false,
        help: 'URL de firma segpres',
        options: [
          { text: 'PRODUCCION', value: 'https://api.firma.digital.gob.cl/firma/v2/files/tickets' },
          { text: 'CERTIFICACION', value: 'https://api.firma.cert.digital.gob.cl/firma/v2/files/tickets' },
          { text: 'Simulada', value: 'simulated' }
        ]
      },
      {
        key: 'io.firma.segpres.app',
        label: 'Applicacion registrada',
        type: 'text',
        required: false,
        help: 'Aplicación registrada'
      },
      {
        key: 'io.firma.segpres.entidad',
        label: 'Entidad de la empresa',
        type: 'text',
        required: true
      },
      {
        key: 'io.firma.segpres.key',
        label: 'Token KEY del API',
        type: 'text',
        required: true,
        help: 'Token KEY del API'
      },
      {
        key: 'io.firma.segpres.secret',
        label: 'Secret del api',
        type: 'password',
        required: true
      },
      {
        key: 'io.firma.segpres.only.distended',
        label: 'Solo se usan desatendida',
        type: 'boolean',
        required: false,
        help: 'Si esta opción está activa el OTP se validará con el de Febos'
      },
      {
        key: 'io.firma.segpres.auto.position',
        label: 'Posicionamiento automatico de estampa',
        type: 'boolean',
        required: false,
        help: 'La estampa se posicionará automáticamente al aplicar la firma'
      }
    ]
  }
];

export default {
  name: 'IntegrationsManager',
  components: {
    FormError
  },
  mixins: [CopyToClipboard],
  data() {
    return {
      loading: false,
      saving: false,
      search: '',
      availableIntegrations: AVAILABLE_INTEGRATIONS,
      selectedIntegration: null,
      // Valores persistidos por parametroId, tal como los devuelve el backend.
      configurations: {},
      form: {
        values: {}
      }
    };
  },
  computed: {
    ...mapGetters('Empresas', [
      'company'
    ]),
    filteredIntegrations() {
      if (!this.search) {
        return this.availableIntegrations;
      }
      const word = this.search.toLowerCase();
      return this.availableIntegrations.filter((integration) => {
        const matchName = integration.name.toLowerCase().includes(word);
        const matchDescription = integration.description
          && integration.description.toLowerCase().includes(word);
        return matchName || matchDescription;
      });
    }
  },
  methods: {
    /** Devuelve los parametroId reales de una integración (sin virtuales, con targets). */
    integrationParameterIds(integration) {
      const ids = [
        ...(integration.fields || [])
          .filter((field) => !field.virtual)
          .map((field) => field.key),
        ...(integration.composites || []).map((composite) => composite.target)
      ];
      return [...new Set(ids)];
    },
    /** Decodifica un base64 "usuario:clave" en { user, pass }. */
    decodeCredentials(base64Value) {
      if (!base64Value) {
        return { user: '', pass: '' };
      }
      try {
        const decoded = atob(base64Value);
        const separatorIndex = decoded.indexOf(':');
        if (separatorIndex === -1) {
          return { user: decoded, pass: '' };
        }
        return {
          user: decoded.slice(0, separatorIndex),
          pass: decoded.slice(separatorIndex + 1)
        };
      } catch (error) {
        return { user: '', pass: '' };
      }
    },
    /** Codifica usuario y clave en un base64 "usuario:clave". */
    encodeCredentials(user, pass) {
      if (!user && !pass) {
        return '';
      }
      return btoa(`${user || ''}:${pass || ''}`);
    },
    /**
     * Resuelve el valor persistido para un campo, considerando los campos
     * virtuales que se derivan de un parámetro compuesto (base64 usuario:clave).
     */
    storedValueForField(integration, field) {
      if (!field.virtual) {
        return this.configurations[field.key];
      }
      const composite = (integration.composites || []).find(
        (item) => item.userField === field.key || item.passField === field.key
      );
      if (!composite) {
        return undefined;
      }
      const decoded = this.decodeCredentials(this.configurations[composite.target]);
      return composite.userField === field.key ? decoded.user : decoded.pass;
    },
    /**
     * Estado de configuración de una integración según sus campos requeridos:
     * 'full' (todos cargados), 'partial' (algunos) o 'empty' (ninguno).
     */
    integrationStatus(integration) {
      const requiredFields = (integration.fields || []).filter((field) => field.required);
      if (!requiredFields.length) {
        return 'empty';
      }
      const filledCount = requiredFields.filter((field) => {
        const value = this.storedValueForField(integration, field);
        return value !== undefined && value !== null && value !== '';
      }).length;
      if (filledCount === 0) {
        return 'empty';
      }
      if (filledCount === requiredFields.length) {
        return 'full';
      }
      return 'partial';
    },
    integrationBadge(integration) {
      const status = this.integrationStatus(integration);
      const badges = {
        full: { variant: 'success', text: 'Configurada' },
        partial: { variant: 'warning', text: 'Configuración parcial' },
        empty: { variant: 'secondary', text: 'No configurada' }
      };
      return badges[status];
    },
    /** Convierte el valor crudo del backend a su representación de formulario. */
    toFormValue(field, rawValue) {
      if (field.type === 'boolean') {
        return rawValue === 'Y' || rawValue === true;
      }
      return rawValue === undefined || rawValue === null ? '' : rawValue;
    },
    /** Convierte el valor del formulario al formato persistido en el backend. */
    toStoredValue(field, formValue) {
      if (field.type === 'boolean') {
        return formValue ? 'Y' : 'N';
      }
      return formValue === undefined || formValue === null ? '' : String(formValue);
    },
    buildFormValues(integration) {
      // Mapa de campo virtual -> rol dentro de un composite ('user' | 'pass').
      const virtualRoles = {};
      (integration.composites || []).forEach((composite) => {
        const decoded = this.decodeCredentials(this.configurations[composite.target]);
        virtualRoles[composite.userField] = decoded.user;
        virtualRoles[composite.passField] = decoded.pass;
      });
      return (integration.fields || []).reduce((accumulator, field) => {
        if (field.virtual) {
          return { ...accumulator, [field.key]: virtualRoles[field.key] || '' };
        }
        return {
          ...accumulator,
          [field.key]: this.toFormValue(field, this.configurations[field.key])
        };
      }, {});
    },
    selectIntegration(integration) {
      this.selectedIntegration = integration;
      this.form = {
        values: this.buildFormValues(integration)
      };
      this.$validator.reset();
    },
    isSelected(integration) {
      return !!this.selectedIntegration && this.selectedIntegration.key === integration.key;
    },
    isSimpleInput(field) {
      return ['text', 'password', 'number', 'url', 'email'].includes(field.type);
    },
    inputType(field) {
      if (field.type === 'password') {
        return 'password';
      }
      if (field.type === 'number') {
        return 'number';
      }
      return 'text';
    },
    validationState(field) {
      if (!field.required) {
        return null;
      }
      const name = `integration.${field.key}`;
      const value = this.form.values[field.key];
      const hasValue = value !== '' && value !== null && value !== undefined;
      return !this.errors.has(name) && hasValue;
    },
    async validateForm() {
      const result = await this.$validator.validateAll('integration');
      return !!result;
    },
    async loadConfigurations() {
      this.loading = true;
      try {
        // Una llamada por integración: cada una envía la lista de sus parámetros
        // y el backend devuelve el set de configuraciones resueltas de una vez.
        const results = await Promise.all(
          this.availableIntegrations.map(async (integration) => {
            const parameters = this.integrationParameterIds(integration);
            if (!parameters.length) {
              return {};
            }
            try {
              const { data } = await ioGetSettings({ parameters });
              return data && data.configuraciones ? data.configuraciones : {};
            } catch (error) {
              return {};
            }
          })
        );
        this.configurations = results.reduce((accumulator, settings) => ({
          ...accumulator,
          ...settings
        }), {});
      } finally {
        this.loading = false;
      }
    },
    async saveIntegration() {
      const isValid = await this.validateForm();
      if (!isValid) {
        return;
      }
      this.saving = true;
      try {
        const fields = this.selectedIntegration.fields || [];
        const composites = this.selectedIntegration.composites || [];

        // Parámetros reales a guardar: { parametroId: valor }.
        const toSave = {};
        fields
          .filter((field) => !field.virtual)
          .forEach((field) => {
            toSave[field.key] = this.toStoredValue(field, this.form.values[field.key]);
          });
        // Campos compuestos: se recodifican a base64 "usuario:clave".
        composites.forEach((composite) => {
          toSave[composite.target] = this.encodeCredentials(
            this.form.values[composite.userField],
            this.form.values[composite.passField]
          );
        });

        await Promise.all(
          Object.keys(toSave).map((parametroId) => ioSaveParameter(
            { origen: 'empresa' },
            { parametroId, valor: toSave[parametroId] }
          ))
        );
        // Reflejar los valores guardados en el estado local.
        this.configurations = { ...this.configurations, ...toSave };
        this.$bvToast.toast(
          'Se ha guardado la configuración de la integración',
          {
            title: '¡Genial!',
            variant: 'primary',
            pauseOnHover: true,
            solid: true,
            autoHideDelay: 4000
          }
        );
      } finally {
        this.saving = false;
      }
    }
  },
  async mounted() {
    await this.loadConfigurations();
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.integration-row {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.active-integration {
  border-left: 3px solid var(--primary, #7367f0);
  background-color: rgba(115, 103, 240, 0.08);
}

.list-group-item {
  transition: background-color 0.2s ease;
}

.info-section {
  border: 1px solid rgba(115, 103, 240, 0.3);
  background-color: rgba(115, 103, 240, 0.05);
  border-radius: 6px;
}

.info-section__header {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  border-bottom: 1px solid rgba(115, 103, 240, 0.2);
  color: var(--primary, #7367f0);
}

.info-section__body {
  padding: 15px;
}

.info-section__group-name {
  font-weight: 600;
  margin-bottom: 6px;
}

.info-section__item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.info-section__label {
  min-width: 70px;
  font-weight: 500;
}

.info-section__value {
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 2px 6px;
  margin: 0 6px;
  word-break: break-all;
}

.info-section__copy {
  cursor: pointer;
  color: var(--primary, #7367f0);
}
</style>
